import AbstractHttpService from '@services/abstractHttpService';

import urls from '@const/urls';
import { FileType } from '@const/consts';
import { getBaseUrl } from '@utils/utils';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  EnvelopeTemplateAnchorOccurrenceMode,
  IBox,
  IBoxCreate,
  IBoxLogoUpdate,
  IBoxMember,
  IBoxUpdate,
  IContactDirectoryEntry,
  IContactDirectoryResolveRecipientRule,
  IContactDirectorySave,
  IEnvelopeField,
  IEnvelopeFieldCreate,
  IEnvelopeFieldReorder,
  IEnvelopeSelectValue,
  IEnvelopeFieldUpdate,
  IEnvelopeTableColumn,
  IEnvelopeTemplate,
  IEnvelopeTemplateGenerateDocumentsRequest,
  IEnvelopeTemplateGenerateDocumentsResponse,
  IEnvelopeTemplateFlowStage,
  IEnvelopeTemplateMarkResolveRequest,
  IEnvelopeTemplateMarkResolveResponse,
  IEnvelopeTemplatePrepositionedMark,
  IEnvelopeTemplatePreviewWorkflowRequest,
  IEnvelopeTemplatePreviewWorkflowResponse,
  IEnvelopeTemplatePreProcessingJobPostRequest,
  IEnvelopeTemplatePreProcessingJobResponse,
  IEnvelopeTemplateReorder,
  IEnvelopeTemplateRecipient,
  IEnvelopeTemplateSave,
  ILabel,
  ILabelCreate,
  ILabelGroup,
  ILabelSave,
  EnvelopeTemplatePageSearchMode,
  EnvelopeTemplatePlacementMode,
  EnvelopeTemplateXPlacement,
  EnvelopeTemplateYPlacement,
  ISubdivision,
  ISubdivisionSave,
  ICurrentWebHook,
  IUser,
  IUserInvite,
  IUserInviteCreateRequest,
  IUserInviteResend,
  IUserInviteUpdate,
  IUserUpdate,
  IWebHook,
} from '@store/modules/boxes/types';

type IEnvelopeTemplateConditionValue = NonNullable<IEnvelopeTemplateRecipient['startConditions']>[number]['value'];

interface IEnvelopeTemplateStartConditionRequest {
  fieldKey: string;
  operator: string;
  value: IEnvelopeTemplateConditionValue;
}

type IEnvelopeTemplateRecipientRequest = Omit<IEnvelopeTemplateRecipient, 'isHostMode' | 'isNoPredefinedUser' | 'startConditions'> & {
  isHostMode: IEnvelopeTemplateRecipient['isHostMode'];
  isNoPredefinedUser: boolean;
  startConditions: IEnvelopeTemplateStartConditionRequest[] | null;
};

type IContactDirectorySaveRequest = Omit<IContactDirectorySave, 'phone'> & {
  phone: number | null;
};

type IContactDirectoryFilterOperation = IContactDirectoryResolveRecipientRule['operation'];

interface IEnvelopeFieldSelectValuesOptions {
  maxValuesToReturn?: number;
  queryLike?: string;
}

const normalizeContactDirectoryEntry = (contact: any): IContactDirectoryEntry => ({
  ...contact,
  phone: contact.phone === null || contact.phone === undefined ? null : String(contact.phone),
});

const normalizeNullableText = (value: string | null | undefined): string | null => {
  const normalized = value?.trim();

  return normalized ? normalized : null;
};

const mapContactDirectorySave = (data: IContactDirectorySave): IContactDirectorySaveRequest => {
  const phoneDigits = data.phone?.replace(/\D/g, '') ?? '';

  return {
    companyName: normalizeNullableText(data.companyName),
    email: normalizeNullableText(data.email)?.toLowerCase() ?? null,
    fullName: normalizeNullableText(data.fullName),
    key: normalizeNullableText(data.key),
    phone: phoneDigits ? Number(phoneDigits) : null,
  };
};

const normalizeEnvelopeTableColumn = (column: any): IEnvelopeTableColumn => ({
  ...column,
  currency: column.currency ?? null,
  decimalPlaces: column.decimalPlaces ?? null,
  systemKey: column.systemKey ?? column.key ?? column.newKey,
});

const normalizeEnvelopeSelectColumn = (column: any) => ({
  ...column,
  currency: column.currency ?? null,
  decimalPlaces: column.decimalPlaces ?? null,
  systemKey: column.systemKey ?? column.key,
});

const normalizeEnvelopeField = (field: any): IEnvelopeField => ({
  ...field,
  currency: field.currency ?? null,
  decimalPlaces: field.decimalPlaces ?? null,
  systemKey: field.systemKey ?? field.key,
  tableColumns: field.tableColumns?.map(normalizeEnvelopeTableColumn) ?? null,
  selectColumns: field.selectColumns?.map(normalizeEnvelopeSelectColumn) ?? null,
  keyColumn: field.keyColumn ?? null,
  displayTemplate: field.displayTemplate ?? null,
  displayTemplateColumnKeys: field.displayTemplateColumnKeys ?? null,
  maxValuesToReturn: field.maxValuesToReturn ?? null,
  isClientSideProcessing: field.isClientSideProcessing ?? null,
});

const mapEnvelopeSelectColumns = (columns: IEnvelopeField['selectColumns']) => (
  columns?.map(({
    currency,
    decimalPlaces,
    isSearchable,
    isVisibleAfterSelection,
    isVisibleInPicker,
    name,
    systemKey,
    type,
  }) => ({
    currency,
    decimalPlaces,
    isSearchable,
    isVisibleAfterSelection,
    isVisibleInPicker,
    key: systemKey,
    name,
    type,
  })) ?? null
);

const mapEnvelopeFieldCreate = (data: IEnvelopeFieldCreate) => ({
  currency: data.currency,
  description: data.description,
  envelopeDirection: data.envelopeDirection,
  isVisibleInListView: data.isVisibleInListView,
  decimalPlaces: data.decimalPlaces,
  key: data.systemKey,
  name: data.name,
  tableColumns: data.tableColumns?.map(({ currency, decimalPlaces, name, systemKey, type }) => ({
    currency,
    decimalPlaces,
    key: systemKey,
    name,
    type,
  })) ?? null,
  type: data.type,
  selectColumns: mapEnvelopeSelectColumns(data.selectColumns),
  keyColumn: data.keyColumn,
  displayTemplate: data.displayTemplate,
  isClientSideProcessing: data.isClientSideProcessing,
});

const mapEnvelopeFieldUpdate = (data: IEnvelopeFieldUpdate) => ({
  currency: data.currency,
  description: data.description,
  envelopeDirection: data.envelopeDirection,
  isVisibleInListView: data.isVisibleInListView,
  key: data.systemKey,
  name: data.name,
  tableColumns: data.tableColumns?.map(({ currency, decimalPlaces, name, oldKey, systemKey, type }) => ({
    currency,
    decimalPlaces,
    name,
    newKey: systemKey,
    oldKey: oldKey === undefined ? systemKey : oldKey,
    type,
  })) ?? null,
  selectColumns: mapEnvelopeSelectColumns(data.selectColumns),
  keyColumn: data.keyColumn,
  displayTemplate: data.displayTemplate,
  isClientSideProcessing: data.isClientSideProcessing,
});

const normalizeEnvelopeTemplateStartCondition = (condition: any) => ({
  ...condition,
  fieldSystemKey: condition.fieldSystemKey ?? condition.fieldKey,
});

const normalizeEnvelopeTemplateEditableWhenCondition = (condition: any) => ({
  ...condition,
  fieldSystemKey: condition.fieldSystemKey ?? condition.fieldKey,
});

const normalizeEnvelopeTemplateEditableWhen = (editableWhen: any) => (
  editableWhen
    ? {
        ...editableWhen,
        conditions: editableWhen.conditions?.map(normalizeEnvelopeTemplateEditableWhenCondition) ?? [],
      }
    : null
);

const normalizeEnvelopeTemplateMark = (mark: any): IEnvelopeTemplatePrepositionedMark => {
  if (mark.placementMode !== EnvelopeTemplatePlacementMode.RELATIVE) return mark;

  return {
    ...mark,
    anchorOccurrenceMode: mark.anchorOccurrenceMode ?? EnvelopeTemplateAnchorOccurrenceMode.FIRST,
    pageSearchMode: mark.pageSearchMode ?? EnvelopeTemplatePageSearchMode.ALL_PAGES,
    xOffset: mark.xOffset ?? 0,
    xPlacement: mark.xPlacement ?? EnvelopeTemplateXPlacement.LEFT,
    yOffset: mark.yOffset ?? 0,
    yPlacement: mark.yPlacement ?? EnvelopeTemplateYPlacement.TOP,
  };
};

const normalizeEnvelopeTemplateRecipient = (recipient: any): IEnvelopeTemplateRecipient => ({
  ...recipient,
  assignedToBoxUserOguid: recipient.assignedToBoxUserOguid ?? null,
  isAssignedToBoxUser: recipient.isAssignedToBoxUser ?? false,
  isHostMode: recipient.isAssignedToBoxUser ? null : recipient.isHostMode ?? false,
  isNoPredefinedUser: recipient.isNoPredefinedUser ?? false,
  signingParams: recipient.signingParams
    ? {
        ...recipient.signingParams,
        isSignOnBehalfOfOrganization: recipient.signingParams.isAllowElectronicSignature
          ? null
          : recipient.signingParams.isSignOnBehalfOfOrganization ??
            recipient.isSignOnBehalfOfOrganization ??
            false,
      }
    : null,
  signer: recipient.signer ?? {
    fullName: '',
    initials: null,
    phone: null,
    position: null,
  },
  recipientKey: recipient.recipientKey ?? null,
  prepositionedMarks: recipient.prepositionedMarks?.map(normalizeEnvelopeTemplateMark) ?? null,
  startConditions: recipient.startConditions?.map(normalizeEnvelopeTemplateStartCondition) ?? null,
});

const getLabelFullKey = (label: string | { fullKey: string }): string => (
  typeof label === 'string' ? label : label.fullKey
);

const normalizeBoxUser = (user: any): IUser => ({
  ...user,
  userBoxOguid: user.userBoxOguid ?? user.boxUserOguid ?? user.oguid,
});

const normalizeRouteNotifications = (notifications: any): IEnvelopeTemplate['notifications'] => (
  notifications
    ? {
        subscriptions: notifications.subscriptions?.map((subscription: any) => {
          return {
            events: subscription.events ?? null,
            notifyOnFinished: subscription.notifyOnFinished ?? false,
            subscriber: {
              type: subscription.subscriber.type,
              boxUserOguid: subscription.subscriber.boxUserOguid ??
                subscription.subscriber.boxUser?.boxUserOguid ??
                null,
              recipientKey: subscription.subscriber.recipientKey ?? null,
            },
          }
        }) ?? null,
      }
    : null
);

const normalizeEnvelopeTemplate = (template: any): IEnvelopeTemplate => ({
  ...template,
  isPreprocessingEnabled: template.isPreprocessingEnabled ?? true,
  clientScript: template.clientScript ?? null,
  labels: template.labels ?? [],
  fields: template.fields?.map((field: any) => ({
    ...field,
    editableWhen: normalizeEnvelopeTemplateEditableWhen(field.editableWhen),
    hasHorizontalSeparatorBefore: field.hasHorizontalSeparatorBefore ?? false,
    isCheckUnique: field.isCheckUnique ?? null,
    systemKey: field.systemKey ?? field.key,
  })) ?? null,
  flow: template.flow?.map((stage: any) => ({
    ...stage,
    description: stage.description ?? null,
    stageKey: stage.stageKey ?? null,
    labels: stage.labels?.map(getLabelFullKey) ?? [],
    recipients: stage.recipients?.map(normalizeEnvelopeTemplateRecipient) ?? [],
  })) ?? null,
  files: template.files?.map((file: any) => ({
    ...file,
    oguid: file.oguid ?? file.fileOguid,
  })) ?? null,
  notifications: normalizeRouteNotifications(template.notifications),
  variables: template.variables ?? null,
});

const mapEnvelopeTemplateStartCondition = (condition: NonNullable<IEnvelopeTemplateRecipient['startConditions']>[number]): IEnvelopeTemplateStartConditionRequest => ({
  fieldKey: condition.fieldSystemKey ?? condition.fieldKey,
  operator: condition.operator,
  value: condition.value,
});

const mapEnvelopeTemplateEditableWhen = (
  editableWhen: NonNullable<IEnvelopeTemplateSave['fields']>[number]['editableWhen']
) => (
  editableWhen?.conditions?.length
    ? {
        operator: editableWhen.operator,
        conditions: editableWhen.conditions.map((condition) => ({
          fieldKey: condition.fieldSystemKey ?? condition.fieldKey,
          operator: condition.operator,
          value: condition.value,
        })),
      }
    : null
);

const relativePlacementFields = [
  'anchorText',
  'pageSearchMode',
  'pageSearchPages',
  'pageSearchFromPage',
  'pageSearchToPage',
  'anchorOccurrenceMode',
  'anchorOccurrenceNumber',
  'xPlacement',
  'xOffset',
  'yPlacement',
  'yOffset',
];

const mapEnvelopeTemplateMark = (mark: IEnvelopeTemplatePrepositionedMark): IEnvelopeTemplatePrepositionedMark => {
  const mapped = { ...mark };

  if (mapped.placementMode === EnvelopeTemplatePlacementMode.RELATIVE) {
    mapped.pageSearchMode = mapped.pageSearchMode ?? EnvelopeTemplatePageSearchMode.ALL_PAGES;
    mapped.anchorOccurrenceMode = mapped.anchorOccurrenceMode ?? EnvelopeTemplateAnchorOccurrenceMode.FIRST;
    mapped.xPlacement = mapped.xPlacement ?? EnvelopeTemplateXPlacement.LEFT;
    mapped.xOffset = mapped.xOffset ?? 0;
    mapped.yPlacement = mapped.yPlacement ?? EnvelopeTemplateYPlacement.TOP;
    mapped.yOffset = mapped.yOffset ?? 0;
    delete mapped.pageNumber;
    delete mapped.topLeftX;
    delete mapped.topLeftY;
    return mapped;
  }

  relativePlacementFields.forEach((field) => {
    delete mapped[field];
  });

  return mapped;
};

const mapEnvelopeTemplateRecipient = (recipient: IEnvelopeTemplateRecipient): IEnvelopeTemplateRecipientRequest => {
  const isNoPredefinedUser = Boolean(recipient.isNoPredefinedUser);
  const isAssignedToBoxUser = Boolean(recipient.isAssignedToBoxUser);
  const assignedToBoxUserOguid = isAssignedToBoxUser && !isNoPredefinedUser
    ? recipient.assignedToBoxUserOguid ?? null
    : null;
  const isSigning = recipient.type === 'SIGNING';
  const prepositionedMarks = (recipient.prepositionedMarks ?? [])
    .filter((mark) => isSigning || mark.type !== 'SIGNATURE')
    .map(mapEnvelopeTemplateMark);

  const signingParams = recipient.signingParams
    ? {
        ...recipient.signingParams,
        isSignOnBehalfOfOrganization: recipient.signingParams.isAllowElectronicSignature
          ? null
          : recipient.signingParams.isSignOnBehalfOfOrganization ??
            recipient.isSignOnBehalfOfOrganization ??
            false,
      }
    : null;

  return {
    assignmentDescription: recipient.assignmentDescription,
    assignedToBoxUserOguid,
    boxId: isNoPredefinedUser || isAssignedToBoxUser ? null : recipient.boxId,
    guestEmail: isNoPredefinedUser || isAssignedToBoxUser ? null : recipient.guestEmail,
    isAssignedToBoxUser,
    isHostMode: isAssignedToBoxUser ? null : recipient.isHostMode ?? false,
    isNoPredefinedUser,
    isRequireSelfie: isSigning && recipient.isRequireSelfie,
    isRequireSms: isSigning && recipient.isRequireSms,
    prepositionedMarks: prepositionedMarks.length ? prepositionedMarks : null,
    recipientKey: recipient.recipientKey ?? null,
    resolveRecipientRule: isAssignedToBoxUser ? null : recipient.resolveRecipientRule ?? null,
    signer: isNoPredefinedUser || isAssignedToBoxUser ? null : recipient.signer,
    signingParams: isSigning ? signingParams : null,
    startConditions: recipient.startConditions?.length
      ? recipient.startConditions.map(mapEnvelopeTemplateStartCondition)
      : null,
    type: recipient.type,
  };
};

const mapRouteNotifications = (
  notifications: IEnvelopeTemplateSave['notifications']
): IEnvelopeTemplateSave['notifications'] => (
  notifications
    ? {
        subscriptions: notifications.subscriptions?.map(({ events, notifyOnFinished, subscriber }) => ({
          events,
          notifyOnFinished,
          subscriber: {
            type: subscriber.type,
            recipientKey: subscriber.recipientKey,
            boxUserOguid: subscriber.boxUserOguid,
          },
        })) ?? null,
      }
    : null
);

const mapEnvelopeTemplateSave = (data: IEnvelopeTemplateSave) => ({
  ...data,
  isPreprocessingEnabled: data.isPreprocessingEnabled ?? true,
  clientScript: data.clientScript?.trim() ? data.clientScript : null,
  labels: data.labels ?? [],
  fields: data.fields?.length
    ? data.fields.map(({
        editableWhen,
        hasHorizontalSeparatorBefore,
        isCheckUnique,
        isOnSameLineAsPrevious,
        isRequired,
        systemKey,
      }) => ({
        editableWhen: mapEnvelopeTemplateEditableWhen(editableWhen),
        hasHorizontalSeparatorBefore: Boolean(hasHorizontalSeparatorBefore),
        isCheckUnique: isCheckUnique ?? null,
        isOnSameLineAsPrevious,
        isRequired,
        key: systemKey,
      }))
    : null,
  files: data.files?.length
    ? data.files.map(({ fileMustBeProvidedByUser, oguid, source }) => ({
        fileMustBeProvidedByUser,
        oguid,
        source,
      }))
    : null,
  notifications: mapRouteNotifications(data.notifications),
  flow: data.flow?.length
    ? data.flow.map((stage) => ({
        description: stage.description ?? null,
        stageKey: stage.stageKey ?? null,
        labels: stage.labels ?? [],
        recipients: stage.recipients.map(mapEnvelopeTemplateRecipient),
      }))
    : null,
  variables: data.variables?.length ? data.variables : null,
});

const mapEnvelopeTemplateMarkResolveRequest = (data: IEnvelopeTemplateMarkResolveRequest) => ({
  ...data,
  prepositionedMarks: data.prepositionedMarks.map(mapEnvelopeTemplateMark),
});

const mapEnvelopeTemplatePreviewWorkflowRequest = (data: IEnvelopeTemplatePreviewWorkflowRequest) => ({
  fields: data.fields,
  variables: data.variables ?? null,
  ...(data.files?.length ? { files: data.files } : {}),
});

class BoxesService extends AbstractHttpService {
  private readonly url = urls.boxes;

  //----------------------------------------------------------------------------------------
  // Хуки
  //----------------------------------------------------------------------------------------
  async getWebHook(boxOguid: string): Promise<AxiosResponse<ICurrentWebHook>> {
    const url = this.url.webHookIntegration.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  async updateWebHook(
    boxOguid: string,
    webHook: IWebHook
  ): Promise<AxiosResponse> {
    const url = this.url.webHookIntegration.replace('{{ boxOguid }}', boxOguid);

    return await this._http.put(url, webHook, {});
  }

  //----------------------------------------------------------------------------------------
  // Бизнес-боксы
  //----------------------------------------------------------------------------------------
  /** Получение списка ящиков пользователя */
  async getBoxes(): Promise<AxiosResponse<IBox[]>> {
    const url = this.url.boxes;

    return await this._http.get(url, {});
  }

  /** Создание бизнес-ящика */
  async createBox(data: IBoxCreate): Promise<AxiosResponse<string>> {
    const url = this.url.boxes;

    return await this._http.post(url, data, {});
  }

  /** Обновление бизнес-ящика */
  async updateBox(updateObj: IBoxUpdate): Promise<AxiosResponse<void>> {
    const url = this.url.changeBox.replace(
      '{{ boxOguid }}',
      updateObj.boxOguid
    );

    return await this._http.put(url, updateObj.data, {});
  }

  /** Удаление бизнес-ящика */
  async deleteBox(boxOguid: string): Promise<AxiosResponse<void>> {
    const url = this.url.changeBox.replace('{{ boxOguid }}', boxOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Лого
  //----------------------------------------------------------------------------------------
  /** Получение лого */
  async getLogo(boxOguid: string): Promise<AxiosResponse<Blob>> {
    const url = this.url.getLogo.replace('{{ boxOguid }}', boxOguid);

    const headers = {
      Accept: 'application/octet-stream, application/json',
    };
    const config: AxiosRequestConfig = {
      params: {
        isNeedResetError: true,
      },
      headers,
      responseType: 'blob',
    };

    return await this._http.get(url, config);
  }

  /** Обновление лого */
  async setLogo(updateObj: IBoxLogoUpdate): Promise<AxiosResponse<void>> {
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    const config: AxiosRequestConfig = { headers };

    const url = this.url.changeLogo.replace(
      '{{ boxOguid }}',
      updateObj.boxOguid
    );

    return await this._http.post(url, updateObj.data, config);
  }

  /** Удаление лого */
  async deleteLogo(boxOguid: string): Promise<AxiosResponse<void>> {
    const url = this.url.changeLogo.replace('{{ boxOguid }}', boxOguid);

    return await this._http.delete(url, {});
  }

  async getLabelGroups(boxOguid: string): Promise<AxiosResponse<ILabelGroup[]>> {
    const url = this.url.labelGroups.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  async createLabelGroup(boxOguid: string, data: ILabelGroup): Promise<AxiosResponse<ILabelGroup>> {
    const url = this.url.labelGroups.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, data, {});
  }

  async updateLabelGroup(boxOguid: string, groupKey: string, data: ILabelGroup): Promise<AxiosResponse<void>> {
    const url = this.url.labelGroups.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ groupKey }}', encodeURIComponent(groupKey));

    return await this._http.put(url, data, {});
  }

  async deleteLabelGroup(boxOguid: string, groupKey: string): Promise<AxiosResponse<void>> {
    const url = this.url.labelGroups.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ groupKey }}', encodeURIComponent(groupKey));

    return await this._http.delete(url, {});
  }

  async reorderLabelGroups(boxOguid: string, keys: string[]): Promise<AxiosResponse<void>> {
    const url = this.url.labelGroups.reorder.replace('{{ boxOguid }}', boxOguid);

    return await this._http.put(url, { keys }, {});
  }

  async getLabels(boxOguid: string): Promise<AxiosResponse<ILabel[]>> {
    const url = this.url.labels.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  async createLabel(boxOguid: string, data: ILabelCreate): Promise<AxiosResponse<ILabel>> {
    const url = this.url.labels.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, data, {});
  }

  async updateLabel(boxOguid: string, labelFullKey: string, data: ILabelSave): Promise<AxiosResponse<void>> {
    const url = this.url.labels.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ labelFullKey }}', encodeURIComponent(labelFullKey));

    return await this._http.put(url, data, {});
  }

  async deleteLabel(boxOguid: string, labelFullKey: string): Promise<AxiosResponse<void>> {
    const url = this.url.labels.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ labelFullKey }}', encodeURIComponent(labelFullKey));

    return await this._http.delete(url, {});
  }

  async reorderLabels(boxOguid: string, keys: string[]): Promise<AxiosResponse<void>> {
    const url = this.url.labels.reorder.replace('{{ boxOguid }}', boxOguid);

    return await this._http.put(url, { keys }, {});
  }

  async assignEnvelopeLabel(boxOguid: string, labelFullKey: string, envelopeOguids: string[]): Promise<AxiosResponse<void>> {
    const url = this.url.labels.assign
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ labelFullKey }}', encodeURIComponent(labelFullKey));

    return await this._http.post(url, { envelopeOguids }, {});
  }

  async removeEnvelopeLabel(boxOguid: string, labelFullKey: string, envelopeOguids: string[]): Promise<AxiosResponse<void>> {
    const url = this.url.labels.remove
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ labelFullKey }}', encodeURIComponent(labelFullKey));

    return await this._http.post(url, { envelopeOguids }, {});
  }

  //----------------------------------------------------------------------------------------
  // Пользователи
  //----------------------------------------------------------------------------------------
  /** Получение списка пользователей текущего бизнес-бокса */
  async getUsers(boxOguid: string): Promise<AxiosResponse<IUser[]>> {
    const url = this.url.users.list.replace('{{ boxOguid }}', boxOguid);

    const response: AxiosResponse<IUser[]> = await this._http.get(url, {});

    return {
      ...response,
      data: response.data.map(normalizeBoxUser),
    };
  }

  /** Получение доступного всем участникам краткого списка пользователей ящика */
  async getMembers(boxOguid: string): Promise<AxiosResponse<IBoxMember[]>> {
    const url = this.url.members.replace('{{ boxOguid }}', boxOguid);
    const response: AxiosResponse<Array<Omit<IBoxMember, 'userBoxOguid'> & { boxUserOguid: string }>> =
      await this._http.get(url, {});

    return {
      ...response,
      data: response.data.map(({ boxUserOguid, ...member }) => ({
        ...member,
        userBoxOguid: boxUserOguid,
      })),
    };
  }

  /** Обновление пользователя текущего бизнес-бокса */
  async putUser(
    boxOguid: string,
    userBoxOguid: string,
    values: IUserUpdate,
  ): Promise<AxiosResponse<void>> {
    const url = this.url.users.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ userBoxOguid }}', userBoxOguid);

    const config: AxiosRequestConfig = {
      params: {
        isNeedResetError: true,
      },
    };

    return await this._http.put(url, values, config);
  }

  /** Удаление пользователя из текущего бизнес-бокса */
  async deleteUser(
    boxOguid: string,
    userBoxOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.users.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ userBoxOguid }}', userBoxOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Приглашения
  //----------------------------------------------------------------------------------------
  /** Список приглашений пользователей */
  async getInvites(boxOguid: string): Promise<AxiosResponse<IUserInvite[]>> {
    const url = this.url.invites.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  /** Создание приглашения пользователю */
  async sendInvite(
    boxOguid: string,
    invite: IUserInviteCreateRequest
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.create.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, invite, {});
  }

  /** Повторение отправки приглашения пользователю */
  async repeatInvites(
    boxOguid: string,
    invites: IUserInviteResend
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.repeat.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, invites, {});
  }

  /** Повторение отправки приглашения пользователю */
  async updateInvite(
    boxOguid: string,
    inviteOguid: string,
    invite: IUserInviteUpdate
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ inviteOguid }}', inviteOguid);

    return await this._http.put(url, invite, {});
  }

  /** Удаления приглашения пользователя */
  async deleteInvite(
    boxOguid: string,
    inviteOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ inviteOguid }}', inviteOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Пользовательские поля конвертов
  //----------------------------------------------------------------------------------------
  async getEnvelopeFields(boxOguid: string): Promise<AxiosResponse<IEnvelopeField[]>> {
    const url = this.url.envelopeFields.list.replace('{{ boxOguid }}', boxOguid);

    const response = await this._http.get(url, {});

    return {
      ...response,
      data: response.data.map(normalizeEnvelopeField),
    };
  }

  async createEnvelopeField(
    boxOguid: string,
    data: IEnvelopeFieldCreate
  ): Promise<AxiosResponse<IEnvelopeField>> {
    const url = this.url.envelopeFields.list.replace('{{ boxOguid }}', boxOguid);

    const response = await this._http.post(url, mapEnvelopeFieldCreate(data), {});

    return {
      ...response,
      data: normalizeEnvelopeField(response.data),
    };
  }

  async getEnvelopeField(
    boxOguid: string,
    fieldKey: string
  ): Promise<AxiosResponse<IEnvelopeField>> {
    const url = this.url.envelopeFields.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ fieldKey }}', fieldKey);

    const response = await this._http.get(url, {});

    return {
      ...response,
      data: normalizeEnvelopeField(response.data),
    };
  }

  async updateEnvelopeField(
    boxOguid: string,
    fieldKey: string,
    data: IEnvelopeFieldUpdate
  ): Promise<AxiosResponse<IEnvelopeField>> {
    const url = this.url.envelopeFields.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ fieldKey }}', fieldKey);

    const response = await this._http.put(url, mapEnvelopeFieldUpdate(data), {});

    return {
      ...response,
      data: normalizeEnvelopeField(response.data),
    };
  }

  async deleteEnvelopeField(
    boxOguid: string,
    fieldKey: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.envelopeFields.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ fieldKey }}', fieldKey);

    return await this._http.delete(url, {});
  }

  async reorderEnvelopeFields(
    boxOguid: string,
    data: IEnvelopeFieldReorder
  ): Promise<AxiosResponse<void>> {
    const url = this.url.envelopeFields.reorder.replace('{{ boxOguid }}', boxOguid);

    return await this._http.put(url, { keys: data.systemKeys }, {});
  }

  async getEnvelopeFieldSelectValues(
    boxOguid: string,
    fieldKey: string,
    options: IEnvelopeFieldSelectValuesOptions = {}
  ): Promise<AxiosResponse<IEnvelopeSelectValue[]>> {
    const url = this.url.envelopeFields.values
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ fieldKey }}', fieldKey);
    const config: AxiosRequestConfig = {
      params: {
        ...(options.maxValuesToReturn ? { maxValuesToReturn: options.maxValuesToReturn } : {}),
        ...(options.queryLike ? { 'query.like': options.queryLike } : {}),
      },
    };

    return await this._http.get(url, config);
  }

  async updateEnvelopeFieldSelectValues(
    boxOguid: string,
    fieldKey: string,
    values: IEnvelopeSelectValue[]
  ): Promise<AxiosResponse<void>> {
    const url = this.url.envelopeFields.values
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ fieldKey }}', fieldKey);

    return await this._http.put(url, values.map(({ fields }) => ({ fields })), {});
  }

  //----------------------------------------------------------------------------------------
  // Подразделения
  //----------------------------------------------------------------------------------------
  async getSubdivisions(boxOguid: string): Promise<AxiosResponse<ISubdivision[]>> {
    const url = this.url.subdivisions.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  async createSubdivision(
    boxOguid: string,
    data: ISubdivisionSave
  ): Promise<AxiosResponse<ISubdivision>> {
    const url = this.url.subdivisions.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, data, {});
  }

  async updateSubdivision(
    boxOguid: string,
    subdivisionOguid: string,
    data: ISubdivisionSave
  ): Promise<AxiosResponse<ISubdivision>> {
    const url = this.url.subdivisions.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ subdivisionOguid }}', subdivisionOguid);

    return await this._http.put(url, data, {});
  }

  async deleteSubdivision(
    boxOguid: string,
    subdivisionOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.subdivisions.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ subdivisionOguid }}', subdivisionOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Адресная книга получателей
  //----------------------------------------------------------------------------------------
  async getContactDirectory(
    boxOguid: string,
    queryLike?: string
  ): Promise<AxiosResponse<IContactDirectoryEntry[]>> {
    const url = this.url.contactDirectory.list.replace('{{ boxOguid }}', boxOguid);
    const config: AxiosRequestConfig = queryLike
      ? {
          params: {
            'query.like': queryLike,
          },
        }
      : {};

    const response = await this._http.get(url, config);

    return {
      ...response,
      data: response.data.map(normalizeContactDirectoryEntry),
    };
  }

  async getContactDirectoryByResolveRule(
    boxOguid: string,
    rule: IContactDirectoryResolveRecipientRule,
    lookupValues: string[]
  ): Promise<AxiosResponse<IContactDirectoryEntry[]>> {
    const uniqueLookupValues = Array.from(new Set(lookupValues.filter(Boolean)));

    if (!uniqueLookupValues.length) {
      return {
        config: {},
        data: [],
        headers: {},
        status: 200,
        statusText: 'OK',
      };
    }

    const getOperation = (): IContactDirectoryFilterOperation => (
      rule.operation === 'eq' && uniqueLookupValues.length > 1
        ? 'in'
        : rule.operation
    );
    const getParams = (operation: IContactDirectoryFilterOperation, values: string[]) => ({
      [`${rule.contactDirectoryLookupKey}.${operation}`]: values.join(','),
    });
    const request = (operation: IContactDirectoryFilterOperation, values: string[]) => (
      this._http.get(
        this.url.contactDirectory.list.replace('{{ boxOguid }}', boxOguid),
        { params: getParams(operation, values) }
      )
    );
    const operation = getOperation();

    if (['like', '%like', 'like%'].includes(operation) && uniqueLookupValues.length > 1) {
      const responses = await Promise.all(uniqueLookupValues.map((value) => request(operation, [value])));
      const response = responses[0];
      const contactsByOguid = responses.reduce<Record<string, IContactDirectoryEntry>>((acc, currentResponse) => {
        currentResponse.data.map(normalizeContactDirectoryEntry).forEach((contact: IContactDirectoryEntry) => {
          acc[contact.oguid] = contact;
        });

        return acc;
      }, {});

      return {
        ...response,
        data: Object.values(contactsByOguid),
      };
    }

    const response = await request(operation, uniqueLookupValues);

    return {
      ...response,
      data: response.data.map(normalizeContactDirectoryEntry),
    };
  }

  async createContactDirectoryEntry(
    boxOguid: string,
    data: IContactDirectorySave
  ): Promise<AxiosResponse<IContactDirectoryEntry>> {
    const url = this.url.contactDirectory.list.replace('{{ boxOguid }}', boxOguid);
    const response = await this._http.post(url, mapContactDirectorySave(data), {});

    return {
      ...response,
      data: normalizeContactDirectoryEntry(response.data),
    };
  }

  async updateContactDirectoryEntry(
    boxOguid: string,
    contactOguid: string,
    data: IContactDirectorySave
  ): Promise<AxiosResponse<IContactDirectoryEntry>> {
    const url = this.url.contactDirectory.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ contactOguid }}', contactOguid);
    const response = await this._http.put(url, mapContactDirectorySave(data), {});

    return {
      ...response,
      data: normalizeContactDirectoryEntry(response.data),
    };
  }

  async deleteContactDirectoryEntry(
    boxOguid: string,
    contactOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.contactDirectory.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ contactOguid }}', contactOguid);

    return await this._http.delete(url, {});
  }

  async clearContactDirectory(boxOguid: string): Promise<AxiosResponse<void>> {
    const url = this.url.contactDirectory.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.delete(url, {});
  }

  async loadContactDirectory(
    boxOguid: string,
    data: IContactDirectorySave[]
  ): Promise<AxiosResponse<void>> {
    const url = this.url.contactDirectory.load.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, data.map(mapContactDirectorySave), {});
  }

  //----------------------------------------------------------------------------------------
  // Шаблоны конвертов
  //----------------------------------------------------------------------------------------
  async getEnvelopeTemplates(boxOguid: string): Promise<AxiosResponse<IEnvelopeTemplate[]>> {
    const url = this.url.envelopeTemplates.list.replace('{{ boxOguid }}', boxOguid);

    const response = await this._http.get(url, {});

    return {
      ...response,
      data: response.data.map(normalizeEnvelopeTemplate),
    };
  }

  async getEnvelopeTemplate(
    boxOguid: string,
    envelopeTemplateOguid: string
  ): Promise<AxiosResponse<IEnvelopeTemplate>> {
    const url = this.url.envelopeTemplates.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid);

    const response = await this._http.get(url, {});

    return {
      ...response,
      data: normalizeEnvelopeTemplate(response.data),
    };
  }

  async createEnvelopeTemplate(
    boxOguid: string,
    data: IEnvelopeTemplateSave
  ): Promise<AxiosResponse<IEnvelopeTemplate>> {
    const url = this.url.envelopeTemplates.list.replace('{{ boxOguid }}', boxOguid);

    const response = await this._http.post(url, mapEnvelopeTemplateSave(data), {});

    return {
      ...response,
      data: normalizeEnvelopeTemplate(response.data),
    };
  }

  async updateEnvelopeTemplate(
    boxOguid: string,
    envelopeTemplateOguid: string,
    data: IEnvelopeTemplateSave
  ): Promise<AxiosResponse<IEnvelopeTemplate>> {
    const url = this.url.envelopeTemplates.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid);

    const response = await this._http.put(url, mapEnvelopeTemplateSave(data), {});

    return {
      ...response,
      data: normalizeEnvelopeTemplate(response.data),
    };
  }

  async deleteEnvelopeTemplate(
    boxOguid: string,
    envelopeTemplateOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.envelopeTemplates.single
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid);

    return await this._http.delete(url, {});
  }

  async reorderEnvelopeTemplates(
    boxOguid: string,
    data: IEnvelopeTemplateReorder
  ): Promise<AxiosResponse<void>> {
    const url = this.url.envelopeTemplates.reorder.replace('{{ boxOguid }}', boxOguid);

    return await this._http.put(url, { oguids: data.oguids }, {});
  }

  async resolveEnvelopeTemplateMarks(
    boxOguid: string,
    data: IEnvelopeTemplateMarkResolveRequest
  ): Promise<AxiosResponse<IEnvelopeTemplateMarkResolveResponse>> {
    const url = this.url.envelopeTemplates.resolveMarks.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, mapEnvelopeTemplateMarkResolveRequest(data), {});
  }

  async getEnvelopeTemplateFilePage(
    boxOguid: string,
    envelopeTemplateOguid: string,
    fileOguid: string,
    page: number,
    requestUuid?: string
  ): Promise<AxiosResponse<string>> {
    const url = this.url.envelopeTemplates.filePage
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid)
      .replace('{{ fileOguid }}', fileOguid)
      .replace('{{ page }}', String(page));

    return await this._http.get(url, {
      params: {
        isNeedResetError: true,
        requestUuid,
      },
    });
  }

  async getEnvelopeTemplateFile(
    boxOguid: string,
    envelopeTemplateOguid: string,
    fileOguid: string
  ): Promise<AxiosResponse<Blob>> {
    const url = this.url.envelopeTemplates.file
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid)
      .replace('{{ fileOguid }}', fileOguid)
      .replace('{{ fileType }}', FileType.ORIGINAL);

    return await this._http.get(url, { responseType: 'blob' });
  }

  getEnvelopeTemplateFilePdfUrl(
    boxOguid: string,
    envelopeTemplateOguid: string,
    fileOguid: string
  ): string {
    const url = this.url.envelopeTemplates.file
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid)
      .replace('{{ fileOguid }}', fileOguid)
      .replace('{{ fileType }}', `${FileType.ORIGINAL}.PDF`);

    return `${getBaseUrl().replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
  }

  async generateEnvelopeTemplateDocuments(
    boxOguid: string,
    envelopeTemplateOguid: string,
    data: IEnvelopeTemplateGenerateDocumentsRequest
  ): Promise<AxiosResponse<IEnvelopeTemplateGenerateDocumentsResponse>> {
    const url = this.url.envelopeTemplates.generateDocuments
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid);

    return await this._http.post(url, data);
  }

  async previewEnvelopeTemplateWorkflow(
    boxOguid: string,
    envelopeTemplateOguid: string,
    data: IEnvelopeTemplatePreviewWorkflowRequest
  ): Promise<AxiosResponse<IEnvelopeTemplatePreviewWorkflowResponse>> {
    const url = this.url.envelopeTemplates.previewWorkflow
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid);

    const response: AxiosResponse<IEnvelopeTemplatePreviewWorkflowResponse> =
      await this._http.post(url, mapEnvelopeTemplatePreviewWorkflowRequest(data), {});

    return {
      ...response,
      data: {
        ...response.data,
        flow: response.data.flow?.map((stage: IEnvelopeTemplateFlowStage) => ({
          ...stage,
          description: stage.description ?? null,
          stageKey: stage.stageKey ?? null,
          labels: stage.labels?.map(getLabelFullKey) ?? [],
          recipients: stage.recipients?.map(normalizeEnvelopeTemplateRecipient) ?? [],
        })) ?? [],
      },
    };
  }

  async startEnvelopeTemplatePreProcessingJob(
    boxOguid: string,
    envelopeTemplateOguid: string,
    data: IEnvelopeTemplatePreProcessingJobPostRequest
  ): Promise<AxiosResponse<string>> {
    const url = this.url.envelopeTemplates.preProcessingJobs
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid);

    return await this._http.post(url, data, {});
  }

  async getEnvelopeTemplatePreProcessingJob(
    boxOguid: string,
    envelopeTemplateOguid: string,
    preProcessingJobOguid: string
  ): Promise<AxiosResponse<IEnvelopeTemplatePreProcessingJobResponse>> {
    const url = this.url.envelopeTemplates.preProcessingJob
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ envelopeTemplateOguid }}', envelopeTemplateOguid)
      .replace('{{ preProcessingJobOguid }}', preProcessingJobOguid);

    return await this._http.get(url, {});
  }
}

export default new BoxesService();
